import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, ExternalLink, RotateCcw } from 'lucide-react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/imageUrl';
import { DEFAULT_VIDEO, FALLBACK_POSTER, withDefaults } from '../../lib/opening';
import { useSlotImage } from '../../lib/useSlotImage';
import ImageUploader from '../../components/admin/ImageUploader';

const VIDEO_MAX_MB = 150;
const same = (a, b) => JSON.stringify(withDefaults(a)) === JSON.stringify(withDefaults(b));

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2 block">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-[#6B6B6B] mt-2 leading-relaxed">{hint}</span>}
  </label>
);

const Slider = ({ label, value, onChange, max = 100, testId }) => (
  <label className="flex items-center gap-4 text-xs text-[#333333]">
    <span className="w-28 shrink-0">{label}</span>
    <input type="range" min={0} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-[#111111]" data-testid={testId} />
    <span className="w-10 text-right tabular-nums">{value}%</span>
  </label>
);

// Miniature of the opening frame with the chosen focus point.
const Frame = ({ cfg, poster, focus, ratio, label, testId }) => (
  <div>
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2 block">{label}</span>
    <div className={`relative overflow-hidden bg-[#111111] ${ratio}`} data-testid={testId}>
      {cfg.videoEnabled && cfg.videoUrl
        ? <video src={resolveImageUrl(cfg.videoUrl)} poster={resolveImageUrl(poster)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `${focus.x}% ${focus.y}%` }} />
        : <img src={resolveImageUrl(poster)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `${focus.x}% ${focus.y}%` }} />}
      <div className="absolute inset-0" style={{ background: `rgba(17,17,17,${cfg.shade / 100})` }} />
    </div>
  </div>
);

const VideoUpload = ({ onUploaded }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    if (!['video/mp4', 'video/quicktime', 'video/webm'].includes(file.type)) { toast.error('Veuillez choisir une vidéo MP4, MOV ou WEBM.'); return; }
    if (file.size > VIDEO_MAX_MB * 1024 * 1024) { toast.error(`Vidéo trop volumineuse (${VIDEO_MAX_MB} Mo maximum).`); return; }
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload-video', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) });
      onUploaded(data.url);
      toast.success('Vidéo importée — pensez à enregistrer le brouillon');
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Échec de l’envoi de la vidéo');
    } finally { setProgress(null); }
  };
  return (
    <>
      <button type="button" disabled={progress !== null} onClick={() => ref.current?.click()} data-testid="opening-video-upload"
        className="inline-flex items-center gap-3 px-5 py-3 border border-[#111111] text-[11px] uppercase tracking-[0.2em] hover:bg-[#111111] hover:text-white transition-colors disabled:opacity-50">
        {progress !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi {progress}%</> : <><Upload className="w-4 h-4" /> Importer une vidéo</>}
      </button>
      <input ref={ref} type="file" accept="video/mp4,video/quicktime,video/webm" className="sr-only" onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} data-testid="opening-video-input" />
    </>
  );
};

const OpeningManager = () => {
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState({});
  const [busy, setBusy] = useState(false);
  const panorama = useSlotImage('hero-panorama', FALLBACK_POSTER);
  const apply = (data) => { setDraft(withDefaults(data.draft)); setPublished(data.published || {}); };
  useEffect(() => { api.get('/admin/opening').then(({ data }) => apply(data)).catch(() => toast.error('Erreur de chargement')); }, []);

  if (!draft) return <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>;
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const setFocus = (k, axis) => (v) => setDraft((d) => ({ ...d, [k]: { ...d[k], [axis]: v } }));
  const poster = draft.posterUrl || panorama;
  const unpublished = !same(draft, published) || !published.videoUrl;

  const run = async (fn, msg) => {
    setBusy(true);
    try { apply((await fn()).data); if (msg) toast.success(msg); return true; } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur'); return false; } finally { setBusy(false); }
  };
  const save = (msg = 'Brouillon enregistré') => run(() => api.put('/admin/opening', draft), msg);
  const preview = async () => { const w = window.open('', '_blank'); if (await save()) w.location = '/?apercu=1'; else w.close(); };
  const publish = async () => { if (await save(null)) run(() => api.post('/admin/opening/publish'), 'Ouverture publiée sur le site'); };

  return (
    <div data-testid="admin-opening-manager">
      <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Page d’accueil</span>
      <h2 className="text-3xl md:text-4xl font-serif text-[#111111]">Ouverture cinématographique</h2>
      <p className="text-sm text-[#6B6B6B] mt-3 mb-4 max-w-2xl leading-relaxed">
        Les réglages sont d’abord enregistrés en brouillon. « Prévisualiser » ouvre la page d’accueil avec le brouillon (visible par vous seulement). « Publier » le rend visible par les visiteurs.
      </p>
      <p className={`text-[11px] uppercase tracking-[0.25em] mb-10 ${unpublished ? 'text-[#B42318]' : 'text-[#6B6B6B]'}`} data-testid="opening-status">
        {unpublished ? 'Brouillon non publié' : 'Le brouillon correspond à la version publiée'}
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-12">
        <div className="space-y-8">
          <label className="flex items-center gap-3 text-sm text-[#111111]">
            <input type="checkbox" checked={draft.videoEnabled} onChange={(e) => set('videoEnabled')(e.target.checked)} className="w-4 h-4 accent-[#111111]" data-testid="opening-video-enabled" />
            Afficher la vidéo (sinon : image de couverture seule)
          </label>
          <Field label="Vidéo d’ouverture" hint={`MP4, MOV ou WEBM — ${VIDEO_MAX_MB} Mo maximum. Conseillé : plan drone de 15 à 40 s, 1920 px, sans son, moins de 20 Mo pour un chargement rapide.`}>
            <div className="flex flex-wrap gap-3 items-center">
              <VideoUpload onUploaded={set('videoUrl')} />
              {draft.videoUrl !== DEFAULT_VIDEO && (
                <button type="button" onClick={() => set('videoUrl')(DEFAULT_VIDEO)} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#6B6B6B] hover:text-[#111111]" data-testid="opening-video-default"><RotateCcw className="w-3.5 h-3.5" /> Vidéo par défaut</button>
              )}
            </div>
            <span className="block text-[11px] text-[#6B6B6B] mt-2 break-all" data-testid="opening-video-current">{draft.videoUrl === DEFAULT_VIDEO ? 'Vidéo actuelle : plan drone libre de droits (Mixkit, licence gratuite)' : `Vidéo actuelle : ${draft.videoUrl}`}</span>
          </Field>
          <Field label="Image de couverture" hint="Affichée pendant le chargement, si la vidéo est désactivée ou si le visiteur limite les animations. Vide = photo « Panorama » des Photos du site.">
            <ImageUploader value={draft.posterUrl} onChange={set('posterUrl')} testId="opening-poster" />
          </Field>
          <Field label="Slogan — ligne 1" hint="Vide = slogan défini dans l’onglet Site.">
            <input value={draft.sloganTop} onChange={(e) => set('sloganTop')(e.target.value)} className="luxury-input-light" data-testid="opening-slogan-top" />
          </Field>
          <Field label="Slogan — ligne 2">
            <input value={draft.sloganBottom} onChange={(e) => set('sloganBottom')(e.target.value)} className="luxury-input-light" data-testid="opening-slogan-bottom" />
          </Field>
          <Field label="Invitation à défiler" hint="Vide = masquée.">
            <input value={draft.scrollLabel} onChange={(e) => set('scrollLabel')(e.target.value)} className="luxury-input-light" data-testid="opening-scroll-label" />
          </Field>
        </div>

        <div className="space-y-8">
          <div className="grid grid-cols-[3fr_1fr] gap-4 items-end">
            <Frame cfg={draft} poster={poster} focus={draft.focusDesktop} ratio="aspect-[16/9]" label="Ordinateur" testId="opening-frame-desktop" />
            <Frame cfg={draft} poster={poster} focus={draft.focusMobile} ratio="aspect-[9/19]" label="Mobile" testId="opening-frame-mobile" />
          </div>
          <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B]">Cadrage ordinateur</p>
            <Slider label="Horizontal" value={draft.focusDesktop.x} onChange={setFocus('focusDesktop', 'x')} testId="opening-desktop-x" />
            <Slider label="Vertical" value={draft.focusDesktop.y} onChange={setFocus('focusDesktop', 'y')} testId="opening-desktop-y" />
          </div>
          <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B]">Cadrage mobile</p>
            <Slider label="Horizontal" value={draft.focusMobile.x} onChange={setFocus('focusMobile', 'x')} testId="opening-mobile-x" />
            <Slider label="Vertical" value={draft.focusMobile.y} onChange={setFocus('focusMobile', 'y')} testId="opening-mobile-y" />
          </div>
          <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B]">Lisibilité du texte</p>
            <Slider label="Assombrir" value={draft.shade} max={70} onChange={set('shade')} testId="opening-shade" />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-12 pt-8 border-t border-[#111111]/10">
        <button onClick={() => save()} disabled={busy} className="px-6 py-3 text-xs uppercase tracking-[0.25em] border border-[#111111] hover:bg-[#F5F5F5] disabled:opacity-40" data-testid="opening-save">Enregistrer le brouillon</button>
        <button onClick={preview} disabled={busy} className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.25em] border border-[#111111] hover:bg-[#F5F5F5] disabled:opacity-40" data-testid="opening-preview">Prévisualiser <ExternalLink className="w-3.5 h-3.5" /></button>
        <button onClick={publish} disabled={busy} className="px-6 py-3 text-xs uppercase tracking-[0.25em] bg-[#111111] text-white hover:bg-[#333333] disabled:opacity-40" data-testid="opening-publish">Publier</button>
      </div>
    </div>
  );
};

export default OpeningManager;
